/**
 * Cookie consent storage and helpers for legal compliance.
 * Consent is stored in localStorage so analytics/scripts can respect user choice.
 */

export const COOKIE_CONSENT_KEY = "keelzo_cookie_consent";
const CONSENT_VERSION = 1;
const CONSENT_MAX_AGE_MS = 365 * 24 * 60 * 60 * 1000; // 12 months

export interface CookieConsentState {
  essential: boolean;   // always true, required for operation
  analytics: boolean;
  preference: boolean;
  advertising: boolean;
  timestamp: number;
  version: number;
}

export function getStoredConsent(): CookieConsentState | null {
  try {
    const raw = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!raw) return null;
    const parsed: CookieConsentState = JSON.parse(raw);
    if (parsed.version !== CONSENT_VERSION) return null;
    if (Date.now() - parsed.timestamp > CONSENT_MAX_AGE_MS) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function setStoredConsent(state: Omit<CookieConsentState, "timestamp" | "version">): void {
  const full: CookieConsentState = {
    ...state,
    essential: true,
    timestamp: Date.now(),
    version: CONSENT_VERSION,
  };
  try {
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(full));
  } catch {
    // ignore quota or disabled localStorage
  }
}

export function hasValidConsent(): boolean {
  return getStoredConsent() !== null;
}

export function isAnalyticsAllowed(): boolean {
  const c = getStoredConsent();
  return c?.analytics === true;
}

export function isPreferenceAllowed(): boolean {
  const c = getStoredConsent();
  return c?.preference === true;
}

export function isAdvertisingAllowed(): boolean {
  const c = getStoredConsent();
  return c?.advertising === true;
}
